import { useEffect, useState } from "react";
import { readCache, writeCacheExt } from "@/lib/swr";
import { Link } from "react-router-dom";
import { Drawer } from "../monitoring/articles/detail";
import { NetworkGraph, FocusView } from "@/components/NetworkGraph";
import { SyncBar } from "@/components/SyncBar";
import { DateRange } from "@/components/DateRange";
import { Highlight } from "@/components/Highlight";
import { Panel } from "@/components/Panel";
import { Stat } from "@/components/Stat";
import { Gauge } from "@/components/viz/Gauge";
import { ProgressRow } from "@/components/viz/ProgressRow";

const M = { pos: "var(--mark-pos)", neu: "var(--mark-neu)", neg: "var(--mark-neg)" };
const BLUE = "var(--mark-c1)";
const TONE_ID: Record<string, string> = { positive: "Positif", neutral: "Netral", negative: "Negatif" };
const EWS_META: Record<string, { label: string; bg: string; fg: string; desc: string }> = {
  crisis:    { label: "Crisis",    bg: "var(--status-danger-bg)",  fg: "var(--status-danger)",  desc: "≥35% negatif & ≥10 artikel" },
  current:   { label: "Current",   bg: "var(--status-warn-bg)",    fg: "var(--status-warn)",    desc: "≥20% negatif" },
  emerging:  { label: "Emerging",  bg: "var(--status-warn-bg)",    fg: "var(--status-warn)",    desc: "negatif naik vs periode sebelumnya" },
  potential: { label: "Potential", bg: "var(--gray-100, #F1F5F9)", fg: "var(--color-text-secondary)", desc: "ada artikel negatif" },
  normal:    { label: "Normal",    bg: "var(--status-success-bg)", fg: "var(--status-success)", desc: "tanpa sinyal negatif" },
};
/* Tiga kanal yang diringkas jadi kartu. Radio sengaja TIDAK diberi kartu (belum
   diminta), tapi tetap ikut dihitung sebagai penyebut persentase di bawah —
   sehingga bila tenant punya artikel radio, ketiga persentase tidak akan
   berjumlah 100% dan datanya tidak hilang tanpa jejak. */
const KANAL = [
  { key: "news_online", label: "Online", icon: "fa-solid fa-globe", tint: "var(--mark-c1)" },
  { key: "print", label: "Cetak", icon: "fa-regular fa-newspaper", tint: "var(--mark-c2)" },
  { key: "tv", label: "TV", icon: "fa-solid fa-tv", tint: "var(--mark-c3)" },
] as const;

const nf = (x: number) => (Math.abs(x ?? 0) >= 100000
  ? Intl.NumberFormat("id-ID", { notation: "compact", maximumFractionDigits: 1 }).format(x)
  : Intl.NumberFormat("id-ID").format(x ?? 0));
const art = (params: Record<string, string | undefined>, start: string, end: string) => {
  const p = new URLSearchParams({ start, end });
  for (const [k, v] of Object.entries(params)) if (v) p.set(k, v);
  return `/a/monitoring/articles?${p}`;
};

export default function AnalyticsPage() {
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [net, setNet] = useState<any>(null);
  const [netFocus, setNetFocus] = useState<any>(null);
  const [persons, setPersons] = useState<any[]>([]);
  const [words, setWords] = useState<any[]>([]);
  const [selPerson, setSelPerson] = useState<any | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const defStart = new Date(Date.now() + 7 * 3600e3 - 6 * 86400e3).toISOString().slice(0, 10);
  const [start, setStart] = useState(defStart);
  const [end, setEnd] = useState(today);

  async function load(sv?: string, ev?: string) {
    const S = sv ?? start, E = ev ?? end;
    setErr(null); setSelPerson(null); setNetFocus(null);
    const get = (u: string) => fetch(u).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    get(`/api/v1/persons/top?start=${S}&end=${E}`).then((j) => setPersons(j?.data ?? []));
    get(`/api/v1/analytics/wordcloud?start=${S}&end=${E}`).then((j) => setWords(j?.data ?? []));
    // BUG LAMA: graf jaringan tokoh tidak pernah diambil sehingga kartunya selalu kosong.
    get(`/api/v1/analytics/network?start=${S}&end=${E}`).then((j) => setNet(j?.data ?? null));
    const url = `/api/v1/analytics/overview?start=${S}&end=${E}`;
    const cached = readCache(url);
    if (cached) setD(cached); // tampil instan dari cache tab, lalu diperbarui di bawah
    const r = await fetch(url);
    if (r.status === 401) { location.href = "/login"; return; }
    const text = await r.text();
    if (!r.ok) { setErr(`HTTP ${r.status}: ${text.slice(0, 200)}`); return; }
    const fresh = JSON.parse(text); setD(fresh); writeCacheExt(url, fresh);
  }
  useEffect(() => { load(); }, []);

  const t = d?.tones ?? { positive: 0, neutral: 0, negative: 0 };
  const tot = t.positive + t.neutral + t.negative;
  const cats = (d?.byCategory ?? []) as any[];
  const catSum = cats.reduce((a, c) => a + c.total, 0) || 1;
  const media = (d?.byMedia ?? []) as any[];
  const mediaSum = media.reduce((a, c) => a + c.total, 0) || 1;
  const ews = (d?.ews ?? []) as any[];
  const trending = (d?.trending ?? []) as any[];
  const kanal = (d?.bySourceType ?? []) as Array<{ sourceType: string; total: number; prevTotal: number }>;
  const kanalOf = (k: string) => kanal.find((x) => x.sourceType === k) ?? { total: 0, prevTotal: 0 };
  const kanalTotal = kanal.reduce((a, x) => a + x.total, 0);
  const tiers = (d?.byTier ?? []) as Array<{ tier: number | null; positive: number; negative: number; neutral: number; total: number }>;
  const tierSum = tiers.reduce((a, x) => a + x.total, 0) || 1;
  const TIER_LABEL: Record<string, string> = { "1": "Tier 1 · Nasional", "2": "Tier 2 · Regional", "3": "Tier 3 · Lokal", none: "Tanpa tier" };

  return (
    <div className="avio-rise">
      <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-6)", flexWrap: "wrap" }}>
        <div>
          <h1 className="avio-page-title" style={{ margin: 0 }}>Analytics</h1>
          <div className="avio-t-sm avio-muted">Media massa · {start} → {end} (WIB)</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <DateRange start={start} end={end} onApply={(s2, e2) => { setStart(s2); setEnd(e2); load(s2, e2); }} />
        </div>
      </div>
      <SyncBar wing="media" />
      {err && <div className="avio-banner" data-tone="danger"><i className="fa-solid fa-triangle-exclamation avio-banner__icon" /><div>{err}</div></div>}

      {!d ? (
        <div className="avio-kpis">
          {[0, 1, 2].map((i) => <div key={i} className="avio-sk" style={{ height: 108, borderRadius: "var(--radius-md)" }} />)}
        </div>
      ) : (
        <div className="avio-kpis">
          {KANAL.map((c) => {
            const v = kanalOf(c.key);
            // Delta hanya bermakna bila periode sebelumnya punya angka. prevTotal=0
            // menghasilkan pembagian nol dan "naik tak hingga persen" — lebih jujur
            // tidak menampilkan panah sama sekali.
            const delta = v.prevTotal > 0 ? ((v.total - v.prevTotal) / v.prevTotal) * 100 : null;
            return (
              <Stat key={c.key} label={c.label} icon={c.icon} tint={c.tint} value={v.total}
                sub={kanalTotal > 0 ? `${((v.total / kanalTotal) * 100).toFixed(1)}% dari media massa` : "belum ada data"}
                deltaPct={delta}
                href={art({ sourceType: c.key }, start, end)} />
            );
          })}
        </div>
      )}

      {!d ? (
        <div className="avio-grid">
          {[4, 8, 6, 6].map((c, i) => <div key={i} data-col={String(c)}><div className="avio-sk" style={{ height: 280, borderRadius: "var(--radius-md)" }} /></div>)}
        </div>
      ) : (
      <div className="avio-grid">
        {/* ---------- TONE ---------- */}
        <div data-col="4">
          <Panel icon="fa-solid fa-gauge-high" title="Distribusi tone" hint={`${nf(tot)} artikel`}>
            <div className="avio-gauges">
              <Gauge value={t.positive} total={tot} color={M.pos} label="Positif" />
              <Gauge value={t.neutral} total={tot} color={M.neu} label="Netral" />
              <Gauge value={t.negative} total={tot} color={M.neg} label="Negatif" />
            </div>
            <div className="avio-leg" style={{ marginTop: "var(--space-7)", paddingTop: "var(--space-5)", borderTop: "1px solid var(--color-border)", justifyContent: "space-between" }}>
              {(["positive", "neutral", "negative"] as const).map((k) => (
                <Link key={k} to={art({ sentiment: k }, start, end)} className="avio-leg__i">
                  <span className="avio-leg__sw" style={{ background: k === "positive" ? M.pos : k === "neutral" ? M.neu : M.neg }} />
                  {TONE_ID[k]} <b>{nf(t[k])}</b>
                </Link>
              ))}
            </div>
          </Panel>
        </div>

        {/* ---------- EWS ---------- */}
        <div data-col="8">
          <Panel icon="fa-solid fa-triangle-exclamation" title="Early Warning System" hint="rule-based v1 · ambang deterministik" flush>
            <div className="avio-list">
              {ews.map((e: any) => {
                const m = EWS_META[e.status] ?? EWS_META.potential;
                return (
                  <Link key={e.ref} className="avio-item" title={m.desc}
                    to={art({ category: e.ref, sentiment: e.status === "normal" ? undefined : "negative" }, start, end)}>
                    <span className="avio-chip" data-shape="pill" style={{ background: m.bg, color: m.fg, minWidth: 84, justifyContent: "center" }}>{m.label}</span>
                    <span className="avio-item__main">
                      <span className="avio-item__title">{e.ref}</span>
                      <span className="avio-item__meta">{m.desc}</span>
                    </span>
                    <span className="avio-prow__track" style={{ width: 140, flex: "none" }}>
                      <span className="avio-prow__fill" style={{ flex: e.negative, background: M.neg }} />
                      <span className="avio-prow__fill" style={{ flex: Math.max(0, e.total - e.negative), background: "var(--mark-grid)" }} />
                    </span>
                    <span className="avio-item__side">{e.negative}/{e.total}<br /><span className="avio-muted">{e.sharePct}% neg</span></span>
                  </Link>
                );
              })}
              {ews.length === 0 && <div className="avio-empty"><i className="fa-regular fa-circle-check avio-empty__icon" /><div className="avio-empty__title">Belum ada kategori terpantau.</div></div>}
            </div>
          </Panel>
        </div>

        {/* ---------- KATEGORI ---------- */}
        <div data-col="6">
          <Panel icon="fa-solid fa-eye" title="Media visibility" hint="jumlah artikel per keyword">
            <div className="avio-plist">
              {cats.map((c: any) => (
                <ProgressRow key={c.ref} name={c.ref} href={art({ category: c.ref }, start, end)}
                  total={c.total} share={(c.total / catSum) * 100} maxShare={((cats[0]?.total ?? 1) / catSum) * 100}
                  segs={[{ value: c.total, color: BLUE, title: `${c.ref}: ${c.total}` }]} />
              ))}
              {cats.length === 0 && <div className="avio-empty"><i className="fa-regular fa-circle-question avio-empty__icon" /><div className="avio-empty__title">Belum ada kategori.</div></div>}
            </div>
          </Panel>
        </div>
        <div data-col="6">
          <Panel icon="fa-solid fa-layer-group" title="Tone per kategori" hint="klik segmen untuk lihat artikelnya">
            <div className="avio-plist">
              {cats.map((c: any) => (
                <ProgressRow key={c.ref} name={c.ref} href={art({ category: c.ref }, start, end)}
                  total={c.total} share={(c.total / catSum) * 100} maxShare={((cats[0]?.total ?? 1) / catSum) * 100}
                  segs={[
                    { value: c.positive, color: M.pos, title: `positif ${c.positive}` },
                    { value: c.neutral, color: M.neu, title: `netral ${c.neutral}` },
                    { value: c.negative, color: M.neg, title: `negatif ${c.negative}` },
                  ]} />
              ))}
            </div>
          </Panel>
        </div>

        {/* ---------- MEDIA ---------- */}
        <div data-col="12">
          <Panel icon="fa-solid fa-newspaper" title="Tone per media" hint="14 media teratas">
            <div className="avio-plist" style={{ columnCount: media.length > 7 ? 2 : 1, columnGap: "var(--space-9)" }}>
              {media.map((m: any) => (
                <div key={m.sourceId} style={{ breakInside: "avoid", marginBottom: "var(--space-6)" }}>
                  <ProgressRow name={m.name} href={art({ source: m.sourceId, sourceLabel: m.name }, start, end)}
                    total={m.total} share={(m.total / mediaSum) * 100} maxShare={((media[0]?.total ?? 1) / mediaSum) * 100}
                    segs={[
                      { value: m.positive, color: M.pos, title: `positif ${m.positive}` },
                      { value: m.neutral, color: M.neu, title: `netral ${m.neutral}` },
                      { value: m.negative, color: M.neg, title: `negatif ${m.negative}` },
                    ]} />
                </div>
              ))}
              {media.length === 0 && <div className="avio-empty"><i className="fa-regular fa-newspaper avio-empty__icon" /><div className="avio-empty__title">Belum ada data media.</div></div>}
            </div>
          </Panel>
        </div>

        {/* ---------- MEDIA TIER ---------- */}
        <div data-col="12">
          <Panel icon="fa-solid fa-ranking-star" title="Tone per tier media" hint="Tier 1 Nasional · 2 Regional · 3 Lokal (Digivla)">
            <div className="avio-plist" style={{ columnCount: tiers.length > 2 ? 2 : 1, columnGap: "var(--space-9)" }}>
              {tiers.map((tr) => {
                const key = tr.tier != null ? String(tr.tier) : "none";
                return (
                  <div key={key} style={{ breakInside: "avoid", marginBottom: "var(--space-6)" }}>
                    <ProgressRow name={TIER_LABEL[key] ?? `Tier ${key}`}
                      href={tr.tier != null ? art({ tier: String(tr.tier) }, start, end) : art({ tier: "none" }, start, end)}
                      total={tr.total} share={(tr.total / tierSum) * 100} maxShare={((tiers[0]?.total ?? 1) / tierSum) * 100}
                      segs={[
                        { value: tr.positive, color: M.pos, title: `positif ${tr.positive}` },
                        { value: tr.neutral, color: M.neu, title: `netral ${tr.neutral}` },
                        { value: tr.negative, color: M.neg, title: `negatif ${tr.negative}` },
                      ]} />
                  </div>
                );
              })}
              {tiers.length === 0 && <div className="avio-empty"><i className="fa-solid fa-ranking-star avio-empty__icon" /><div className="avio-empty__title">Belum ada data tier media.</div></div>}
            </div>
          </Panel>
        </div>

        {/* ---------- SPOKESPERSON ---------- */}
        <div data-col="12">
          <Panel icon="fa-solid fa-user-tie" title="Tone per narasumber" hint="klik nama untuk pendalaman"
            action={<Link to="/a/analysis/persons">Halaman Top Tokoh <i className="fa-solid fa-arrow-right" /></Link>}>
            <div className="avio-plist" style={{ columnCount: persons.length > 5 ? 2 : 1, columnGap: "var(--space-9)" }}>
              {persons.slice(0, 10).map((pn: any) => {
                const tt = pn.tones ?? { positive: 0, neutral: 0, negative: 0 };
                const pt = tt.positive + tt.neutral + tt.negative || 1;
                const on = selPerson?.person === pn.person;
                return (
                  <div key={pn.person} style={{ breakInside: "avoid", marginBottom: "var(--space-6)" }}>
                    <div className="avio-prow" onClick={() => setSelPerson(on ? null : pn)} style={{ cursor: "pointer" }}>
                      <span className="avio-prow__head">
                        <span className="avio-prow__name" style={{ fontWeight: on ? 600 : 400 }}>
                          <i className={on ? "fa-solid fa-caret-down" : "fa-solid fa-caret-right"} style={{ color: "var(--color-text-muted)", marginRight: 6 }} />
                          {pn.person}
                        </span>
                        <span className="avio-prow__val">{nf(pn.mentions)}</span>
                        <span className="avio-prow__pct">{tt.negative} neg</span>
                      </span>
                      <span className="avio-prow__track">
                        {tt.positive > 0 && <span className="avio-prow__fill" style={{ flex: tt.positive / pt, background: M.pos }} />}
                        {tt.neutral > 0 && <span className="avio-prow__fill" style={{ flex: tt.neutral / pt, background: M.neu }} />}
                        {tt.negative > 0 && <span className="avio-prow__fill" style={{ flex: tt.negative / pt, background: M.neg }} />}
                      </span>
                    </div>
                  </div>
                );
              })}
              {persons.length === 0 && <div className="avio-empty"><i className="fa-regular fa-user avio-empty__icon" /><div className="avio-empty__title">Belum ada data tokoh.</div><div className="avio-empty__hint">Jalankan sync Tokoh di menu Sync Data.</div></div>}
            </div>
            {selPerson && (() => {
              const tt = selPerson.tones ?? { positive: 0, neutral: 0, negative: 0 };
              const pt = tt.positive + tt.neutral + tt.negative;
              return (
                <div style={{ marginTop: "var(--space-6)", paddingTop: "var(--space-6)", borderTop: "1px solid var(--color-border)", display: "flex", gap: "var(--space-9)", alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ minWidth: 150 }}>
                    <div className="avio-t-sm avio-muted">Narasumber</div>
                    <div style={{ fontSize: "var(--text-xl)", fontWeight: "var(--weight-semi)" as any }}>{selPerson.person}</div>
                    <div className="avio-t-sm avio-muted">{nf(pt)} artikel pada rentang ini</div>
                  </div>
                  <div className="avio-gauges" style={{ minWidth: 300, flex: 1, maxWidth: 420 }}>
                    <Gauge value={tt.positive} total={pt} color={M.pos} label="Positif" size={76} />
                    <Gauge value={tt.neutral} total={pt} color={M.neu} label="Netral" size={76} />
                    <Gauge value={tt.negative} total={pt} color={M.neg} label="Negatif" size={76} />
                  </div>
                  {selPerson.sampleQuote && (
                    <blockquote style={{ margin: 0, maxWidth: 420, color: "var(--color-text-secondary)", fontStyle: "italic", borderLeft: "3px solid var(--brand-500)", paddingLeft: "var(--space-6)", fontSize: "var(--text-base)" }}>
                      “{selPerson.sampleQuote.slice(0, 220)}”
                    </blockquote>
                  )}
                  <Link className="avio-btn" data-variant="secondary" data-size="sm" to={art({ person: selPerson.person }, start, end)}>
                    Semua artikel <i className="fa-solid fa-arrow-right" />
                  </Link>
                </div>
              );
            })()}
          </Panel>
        </div>

        {/* ---------- TRENDING ---------- */}
        <div data-col="12">
          <Panel icon="fa-solid fa-fire" title="Pemberitaan berdampak" hint="peringkat |News Value|" flush
            action={<Link to={art({}, start, end)}>Semua artikel <i className="fa-solid fa-arrow-right" /></Link>}>
            {trending.length === 0
              ? <div className="avio-empty"><i className="fa-regular fa-newspaper avio-empty__icon" /><div className="avio-empty__title">Belum ada artikel.</div></div>
              : <div style={{ overflowX: "auto" }}>
                  <table className="avio-tbl">
                    <thead>
                      <tr>
                        <th style={{ width: 42 }}>#</th><th>Judul</th>
                        <th style={{ width: 160 }}>Media</th><th style={{ width: 110 }}>Tone</th>
                        <th style={{ width: 160 }} className="num">News Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trending.slice(0, 8).map((x: any, i: number) => (
                        <tr key={x.id} style={{ cursor: "pointer" }} onClick={() => setDetailId(x.id)}>
                          <td className="avio-sub">{i + 1}</td>
                          <td><b><Highlight text={x.title} /></b></td>
                          <td className="avio-sub">{x.media ?? "—"}</td>
                          <td>{x.sentimentLabel && <span className="avio-chip" data-sentiment={x.sentimentLabel}><span className="avio-chip__label">{TONE_ID[x.sentimentLabel]}</span></span>}</td>
                          <td className="num" style={{ color: (x.newsValue ?? 0) < 0 ? "var(--sent-neg)" : undefined, fontWeight: 500 }}>
                            {x.newsValue == null ? "—" : `${x.newsValue < 0 ? "-" : ""}Rp ${nf(Math.abs(x.newsValue))}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>}
          </Panel>
        </div>

        {/* ---------- WORDCLOUD ---------- */}
        {words.length > 0 && (
          <div data-col="12">
            <Panel icon="fa-solid fa-cloud" title="Wordcloud" hint="dari judul artikel · klik kata untuk mencarinya">
              <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-4) var(--space-6)", alignItems: "baseline", lineHeight: 1.25 }}>
                {(() => {
                  const max = words[0]?.count ?? 1;
                  return words.map((w: any) => {
                    const rel = w.count / max;
                    return (
                      <Link key={w.word} to={`/a/monitoring/articles?${new URLSearchParams({ start, end, q: w.word } as any)}`}
                        title={`${w.word}: ${w.count} artikel`}
                        style={{
                          fontSize: 12 + Math.round(rel * 22),
                          color: rel > 0.6 ? "var(--brand-700)" : rel > 0.3 ? "var(--color-text)" : "var(--color-text-secondary)",
                          fontWeight: rel > 0.5 ? 700 : 500, textDecoration: "none",
                        }}>{w.word}</Link>
                    );
                  });
                })()}
              </div>
            </Panel>
          </div>
        )}

        {/* ---------- JARINGAN TOKOH ---------- */}
        <div data-col="12">
          <Panel icon="fa-solid fa-circle-nodes" title="Jaringan tokoh"
            hint="dua narasumber dikutip di artikel yang sama = terhubung"
            action={netFocus ? <button className="avio-btn" data-variant="ghost" data-size="sm" onClick={() => setNetFocus(null)}><i className="fa-solid fa-arrow-left" /> Kembali</button> : undefined}>
            {!netFocus && net?.nodes?.length ? (
              <NetworkGraph nodes={net.nodes ?? []} edges={net.edges ?? []}
                onNode={(n) => fetch(`/api/v1/analytics/network?focus=${encodeURIComponent(n.id)}&start=${start}&end=${end}`).then((r) => r.json()).then((j) => setNetFocus(j.data))} />
            ) : null}
            {!netFocus && !net?.nodes?.length && (
              <div className="avio-empty">
                <i className="fa-solid fa-circle-nodes avio-empty__icon" />
                <div className="avio-empty__title">Jaringan belum terbentuk.</div>
                <div className="avio-empty__hint">Butuh minimal dua narasumber yang dikutip pada artikel yang sama.</div>
              </div>
            )}
            {netFocus && <FocusView data={netFocus} onBack={() => setNetFocus(null)}
              postHref={art({ person: netFocus.center.id }, start, end)}
              satHref={(id) => art({ person: id }, start, end)} />}
          </Panel>
        </div>
      </div>
      )}
      {detailId && <Drawer id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
